import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSelection, BulkActionBar, BulkSelectAllCheckbox } from "@/components/BulkActions";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Users, Phone, MapPin, ShoppingBag, Euro, AlertCircle, ArrowLeft, Trash2, Edit2, RefreshCw, Upload, FileText, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const Clients = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [formData, setFormData] = useState({ nom: "", telephone: "", email: "", adresse: "", ville: "", code_postal: "", notes: "" });
  
  // Import en masse
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  // Import emails en masse
  const [emailImportOpen, setEmailImportOpen] = useState(false);
  const [emailImportText, setEmailImportText] = useState("");
  const [emailMatchBy, setEmailMatchBy] = useState("telephone");
  const [emailImportResult, setEmailImportResult] = useState(null);
  const [emailImporting, setEmailImporting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleImportEmails = async () => {
    // Parse "identifier;email" per line (tab or semicolon)
    const lines = emailImportText.split("\n").map((s) => s.trim()).filter(Boolean);
    const entries = [];
    for (const line of lines) {
      const parts = line.split(/[\t;,]+/).map((x) => x.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      entries.push({ identifier: parts[0], email: parts[parts.length - 1] });
    }
    if (!entries.length) { toast.error("Aucune entrée valide"); return; }
    setEmailImporting(true);
    try {
      const res = await axios.post(`${API}/clients/emails/bulk`, { entries, match_by: emailMatchBy }, { headers });
      setEmailImportResult(res.data);
      toast.success(`${res.data.updated} emails importés — ${res.data.not_found} non trouvés`);
      fetchClients();
    } catch {
      toast.error("Erreur import emails");
    } finally { setEmailImporting(false); }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { headers });
      setClients(res.data);
    } catch { toast.error("Erreur chargement clients"); }
    finally { setLoading(false); }
  };

  const fetchClientDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/clients/${id}`, { headers });
      setClientDetail(res.data);
    } catch { toast.error("Erreur chargement fiche client"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await axios.put(`${API}/clients/${selectedClient.id}`, formData, { headers });
        toast.success("Client mis à jour !");
      } else {
        await axios.post(`${API}/clients`, formData, { headers });
        toast.success("Client créé !");
      }
      setDialogOpen(false);
      setSelectedClient(null);
      setFormData({ nom: "", telephone: "", email: "", adresse: "", ville: "", code_postal: "", notes: "" });
      fetchClients();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { headers });
      toast.success("Client supprimé");
      fetchClients();
      if (clientDetail?.id === id) setClientDetail(null);
    } catch { toast.error("Erreur suppression"); }
  };

  const handleSync = async () => {
    try {
      const res = await axios.post(`${API}/clients/sync-from-livraisons`, {}, { headers });
      toast.success(res.data.message);
      fetchClients();
    } catch { toast.error("Erreur synchronisation"); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/clients/export`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "clients_topcocoon.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch { toast.error("Erreur lors de l'export"); }
  };

  // Parse import text (supports tab, semicolon, or line format)
  const parseImportText = (text) => {
    if (!text.trim()) return [];
    
    const lines = text.trim().split("\n");
    const clients = [];
    
    for (const line of lines) {
      if (!line.trim()) continue;
      
      // Try tab-separated (Nom\tTel\tAdresse\tVille\tCP)
      let parts = line.split("\t");
      if (parts.length < 2) {
        // Try semicolon
        parts = line.split(";");
      }
      if (parts.length < 2) {
        // Single line = just name
        clients.push({ nom: line.trim(), telephone: "", adresse: "", ville: "", code_postal: "" });
      } else {
        clients.push({
          nom: parts[0]?.trim() || "",
          telephone: parts[1]?.trim().replace(/\s/g, "") || "",
          adresse: parts[2]?.trim() || "",
          ville: parts[3]?.trim() || "",
          code_postal: parts[4]?.trim() || "",
          notes: parts[5]?.trim() || "Client historique",
        });
      }
    }
    
    return clients;
  };

  const handleImportTextChange = (text) => {
    setImportText(text);
    setImportPreview(parseImportText(text));
  };

  const handleImportSubmit = async () => {
    if (importPreview.length === 0) {
      toast.error("Aucun client à importer");
      return;
    }
    
    setImporting(true);
    try {
      const res = await axios.post(`${API}/clients/import-bulk`, { clients: importPreview }, { headers });
      toast.success(res.data.message);
      setImportDialogOpen(false);
      setImportText("");
      setImportPreview([]);
      fetchClients();
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setFormData({ nom: client.nom, telephone: client.telephone, email: client.email || "", adresse: client.adresse || "", ville: client.ville || "", code_postal: client.code_postal || "", notes: client.notes || "" });
    setDialogOpen(true);
  };

  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter(c =>
    c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.telephone?.includes(searchTerm) ||
    c.ville?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const bulkSel = useBulkSelection(filtered);

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${API}/clients/bulk-delete`, { ids: bulkSel.selectedIds }, { headers });
      toast.success(`${bulkSel.selectedIds.length} client(s) déplacé(s) vers la corbeille`);
      bulkSel.clear();
      fetchClients();
    } catch { toast.error("Erreur suppression"); }
  };

  const totalDepense = clients.reduce((a, c) => a + (c.total_depense || 0), 0);
  const totalImpayes = clients.reduce((a, c) => a + (c.montant_impaye || 0), 0);

  if (clientDetail) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
        <Button variant="ghost" onClick={() => setClientDetail(null)} data-testid="back-to-clients-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux clients
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">{clientDetail.nom}</h1>
            <div className="flex gap-4 mt-2 text-slate-500 text-sm">
              {clientDetail.telephone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{clientDetail.telephone}</span>}
              {clientDetail.ville && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{clientDetail.adresse}, {clientDetail.code_postal} {clientDetail.ville}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Commandes</p><p className="text-2xl font-bold text-slate-800">{clientDetail.nombre_commandes}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Total dépensé</p><p className="text-2xl font-bold text-emerald-600">{(clientDetail.total_depense || 0).toFixed(2)} €</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Email</p><p className="text-lg text-slate-700">{clientDetail.email || "—"}</p></CardContent></Card>
        </div>
        {clientDetail.notes && (
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500 mb-1">Notes</p><p className="text-slate-700">{clientDetail.notes}</p></CardContent></Card>
        )}
        <Card>
          <CardHeader><CardTitle className="text-lg">Historique des commandes</CardTitle></CardHeader>
          <CardContent>
            {(clientDetail.livraisons || []).length === 0 ? (
              <p className="text-center py-8 text-slate-500">Aucune commande</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Commande</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Prix</TableHead>
                    <TableHead className="text-right">Encaissé</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientDetail.livraisons.map((l) => (
                    <TableRow key={l.id} data-testid={`client-livraison-${l.id}`}>
                      <TableCell className="text-sm">{l.date_creation ? new Date(l.date_creation).toLocaleDateString('fr-FR') : "—"}</TableCell>
                      <TableCell className="text-sm">{l.details_commande}</TableCell>
                      <TableCell>
                        <Badge variant={l.statut === "livree" ? "default" : l.statut === "reportee" ? "secondary" : "outline"}
                          className={l.statut === "livree" ? "bg-emerald-100 text-emerald-700" : l.statut === "annulee" ? "bg-red-100 text-red-700" : ""}>
                          {l.statut}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{(l.prix || 0).toFixed(2)} €</TableCell>
                      <TableCell className="text-right">{(l.montant_encaisse != null ? l.montant_encaisse : l.prix || 0).toFixed(2)} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Clients</h1>
          <p className="text-slate-500 mt-1">Gestion et suivi de la clientèle</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleSync} data-testid="sync-clients-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Sync livraisons
          </Button>
          <Button variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" onClick={handleExport} data-testid="export-clients-btn">
            <Download className="w-4 h-4 mr-2" /> Exporter (Excel)
          </Button>
          
          {/* Import en masse dialog */}
          <Dialog open={emailImportOpen} onOpenChange={(o) => { setEmailImportOpen(o); if (!o) { setEmailImportResult(null); setEmailImportText(""); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-emerald-200 text-emerald-600 hover:bg-emerald-50" data-testid="import-emails-btn">
                <Upload className="w-4 h-4 mr-2" /> Importer emails
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-emails-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-600" />
                  Importer des emails clients
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-700 font-medium">Format accepté :</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    Une ligne par client : <strong>Identifiant ; Email</strong> (séparateurs tabulation ou ; ou ,)
                  </p>
                  <p className="text-xs text-slate-600 mt-2 font-mono bg-white p-2 rounded">
                    0477123456 ; marie@example.com<br/>
                    0478654321 ; jean@gmail.com
                  </p>
                </div>

                <div>
                  <Label>Identifiant = </Label>
                  <div className="flex gap-2 mt-1">
                    <Button variant={emailMatchBy === "telephone" ? "default" : "outline"} size="sm" onClick={() => setEmailMatchBy("telephone")} data-testid="email-match-tel">Téléphone</Button>
                    <Button variant={emailMatchBy === "nom" ? "default" : "outline"} size="sm" onClick={() => setEmailMatchBy("nom")} data-testid="email-match-nom">Nom complet</Button>
                  </div>
                </div>

                <div>
                  <Label>Collez votre liste</Label>
                  <Textarea
                    value={emailImportText}
                    onChange={(e) => setEmailImportText(e.target.value)}
                    placeholder={emailMatchBy === "telephone" ? "0477123456 ; marie@example.com" : "Marie Dupont ; marie@example.com"}
                    rows={8}
                    className="font-mono text-sm"
                    data-testid="email-import-textarea"
                  />
                </div>

                {emailImportResult && (
                  <div className="text-sm space-y-2">
                    <div className="p-2 bg-emerald-50 border border-emerald-200 rounded">
                      ✅ {emailImportResult.updated} emails mis à jour
                    </div>
                    {emailImportResult.not_found > 0 && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded">
                        ⚠️ {emailImportResult.not_found} non trouvés — créez d'abord les clients manquants, ou vérifiez les identifiants.
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEmailImportOpen(false)}>Fermer</Button>
                  <Button onClick={handleImportEmails} disabled={emailImporting || !emailImportText.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="email-import-submit-btn">
                    {emailImporting ? "Import…" : "Importer"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="import-clients-btn">
                <Upload className="w-4 h-4 mr-2" /> Importer clients
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-indigo-600" />
                  Importer des anciens clients
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-sm text-indigo-700 font-medium">Format accepté :</p>
                  <p className="text-xs text-indigo-600 mt-1">
                    Une ligne par client, colonnes séparées par <strong>tabulation</strong> ou <strong>;</strong>
                  </p>
                  <p className="text-xs text-slate-500 mt-2 font-mono bg-white p-2 rounded">
                    Nom ; Téléphone ; Adresse ; Ville ; Code postal ; Notes
                  </p>
                </div>
                
                <div>
                  <Label>Collez vos clients ici</Label>
                  <Textarea
                    value={importText}
                    onChange={(e) => handleImportTextChange(e.target.value)}
                    placeholder="Marie Dupont ; 0477123456 ; Rue des Fleurs 12 ; Bruxelles ; 1000
Jean Martin ; 0478654321 ; Avenue du Parc 5 ; Liège ; 4000"
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>
                
                {importPreview.length > 0 && (
                  <div>
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Aperçu ({importPreview.length} clients)
                    </Label>
                    <div className="border rounded-lg max-h-48 overflow-y-auto mt-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Nom</TableHead>
                            <TableHead className="text-xs">Téléphone</TableHead>
                            <TableHead className="text-xs">Ville</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.slice(0, 10).map((c, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm py-1">{c.nom || "—"}</TableCell>
                              <TableCell className="text-sm py-1">{c.telephone || "—"}</TableCell>
                              <TableCell className="text-sm py-1">{c.ville || "—"}</TableCell>
                            </TableRow>
                          ))}
                          {importPreview.length > 10 && (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-xs text-slate-500 py-2">
                                ... et {importPreview.length - 10} autres
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={handleImportSubmit} 
                  disabled={importPreview.length === 0 || importing}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importer {importPreview.length} client(s)
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setSelectedClient(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="client-dialog">
              <DialogHeader><DialogTitle>{selectedClient ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Nom *</Label><Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required data-testid="client-nom-input" /></div>
                  <div className="space-y-1"><Label>Téléphone</Label><Input value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} data-testid="client-telephone-input" /></div>
                </div>
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" /></div>
                <div className="space-y-1"><Label>Adresse</Label><Input value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Code postal</Label><Input value={formData.code_postal} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Ville</Label><Input value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} /></div>
                </div>
                <div className="space-y-1"><Label>Notes</Label><Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-client-btn">{selectedClient ? "Enregistrer" : "Créer"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Total clients</p><p className="text-2xl font-bold text-slate-800" data-testid="total-clients">{clients.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">CA total clients</p><p className="text-2xl font-bold text-emerald-600">{totalDepense.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Impayés total</p><p className="text-2xl font-bold text-red-600">{totalImpayes.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Panier moyen</p><p className="text-2xl font-bold text-indigo-600">{clients.length > 0 ? (totalDepense / clients.length).toFixed(2) : "0.00"} €</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input placeholder="Rechercher par nom, téléphone, ville..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-clients" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="clients-table-card">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" />Liste des clients ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {bulkSel.count > 0 && (
            <BulkActionBar
              count={bulkSel.count}
              onClear={bulkSel.clear}
              onDelete={handleBulkDelete}
              itemLabel="client"
            />
          )}
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun client trouvé</p>
              <p className="text-sm mt-1">Créez un client ou synchronisez depuis les livraisons</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <BulkSelectAllCheckbox allChecked={bulkSel.allChecked} someChecked={bulkSel.someChecked} onToggle={bulkSel.toggleAll} />
                  </TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead className="text-right">Commandes</TableHead>
                  <TableHead className="text-right">Total dépensé</TableHead>
                  <TableHead className="text-right">Impayé</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className={`hover:bg-slate-50 ${bulkSel.isSelected(c.id) ? "bg-indigo-50" : ""}`} data-testid={`client-row-${c.id}`}>
                    <TableCell>
                      <Checkbox checked={bulkSel.isSelected(c.id)} onCheckedChange={() => bulkSel.toggleOne(c.id)} onClick={(e) => e.stopPropagation()} data-testid={`check-client-${c.id}`} />
                    </TableCell>
                    <TableCell className="font-medium cursor-pointer" onClick={() => fetchClientDetail(c.id)}>{c.nom}</TableCell>
                    <TableCell className="cursor-pointer" onClick={() => fetchClientDetail(c.id)}>{c.telephone || "—"}</TableCell>
                    <TableCell className="cursor-pointer" onClick={() => fetchClientDetail(c.id)}>{c.ville || "—"}</TableCell>
                    <TableCell className="text-right cursor-pointer" onClick={() => fetchClientDetail(c.id)}><Badge variant="secondary">{c.nombre_commandes || 0}</Badge></TableCell>
                    <TableCell className="text-right font-medium text-emerald-600 cursor-pointer" onClick={() => fetchClientDetail(c.id)}>{(c.total_depense || 0).toFixed(2)} €</TableCell>
                    <TableCell className="text-right cursor-pointer" onClick={() => fetchClientDetail(c.id)}>
                      {(c.montant_impaye || 0) > 0 ? <span className="text-red-600 font-medium">{(c.montant_impaye).toFixed(2)} €</span> : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Clients;
